import { useQuery } from 'react-query';
import { useTripDesignerConfig } from '../config/TripDesignerConfigProvider';

interface ExperiencePricingQueryParams {
  filters: unknown;
  locale?: string;
  enabled: boolean;
}

// Prices one experience instance for a party and a date range. The answer is
// never reused: a price shown for a program must come from a request made for
// exactly these filters, so nothing is cached, retried or refetched behind the
// user's back. Like the `useAsync` request the day used to make, the previous
// answer stays readable while a new one loads, and `isLoading` covers every
// request in flight, not only the first.
export function useExperiencePricingQuery({
  filters,
  locale,
  enabled,
}: ExperiencePricingQueryParams) {
  const { api } = useTripDesignerConfig();

  const query = useQuery(
    ['experiencePricing', filters, locale],
    () => api.getExperiencesPricing({ filters, locale }),
    {
      enabled,
      retry: false,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  return {
    data: query.data as any,
    isLoading: query.isFetching,
    // `data` is still the answer to the previous filters while a new request
    // is in flight.
    isPreviousData: query.isPreviousData,
  };
}
